<template>
  <v-dialog
    :value="value"
    persistent
    scrollable
    max-width="900"
    @input="$emit('input', $event)"
  >
    <v-card rounded="0" class="bulk-edit-card">
      <v-toolbar color="kenyaGreen" dark flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('memberDataSection.greenCoffee.bulkImport.editRecordTitle') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="bulk-edit-body px-6 py-5">
        <p v-if="addedBy" class="text--secondary mb-5">
          {{ $t('memberDataSection.greenCoffee.addedBy') }} {{ addedBy }}
        </p>

        <!-- Header fields -->
        <v-row dense>
          <v-col
            v-for="field in headerFields"
            :key="field.key"
            cols="12"
            md="6"
          >
            <div class="d-flex align-end justify-space-between mb-1">
              <span class="text-body-2 black--text">{{ $t(field.labelKey) }}</span>
              <span v-if="field.unit" class="text-body-2 text--secondary">{{ field.unit }}</span>
            </div>
            <v-text-field
              v-model="form[field.key]"
              :type="field.type"
              outlined
              dense
              hide-details="auto"
              :error="hasFieldError(field.key)"
              :messages="fieldMessages(field.key)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Quality Control (one block per milling-output lot) -->
        <h3 class="bulk-edit-section-title font-weight-bold mb-4">
          {{ $t('memberDataSection.greenCoffee.bulkImport.qualityControl') }}
        </h3>

        <p
          v-if="hasFieldError('qualityGradings')"
          class="error--text text-body-2 mb-4"
        >
          {{ fieldMessages('qualityGradings').join(' ') }}
        </p>

        <template v-if="form.qualityGradings.length">
          <div
            v-for="(lot, index) in form.qualityGradings"
            :key="index"
            class="bulk-edit-lot"
          >
            <v-row dense>
              <v-col
                v-for="field in gradingFields"
                :key="field.key"
                cols="12"
                md="6"
              >
                <div class="d-flex align-end justify-space-between mb-1">
                  <span class="text-body-2 black--text">{{ $t(field.labelKey) }}</span>
                  <span v-if="field.unit" class="text-body-2 text--secondary">{{ field.unit }}</span>
                </div>
                <v-text-field
                  v-model="lot[field.key]"
                  :type="field.type"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
            <v-divider
              v-if="index < form.qualityGradings.length - 1"
              class="my-5"
            ></v-divider>
          </div>
        </template>
        <p v-else class="text--secondary text-body-2">
          {{ $t('memberDataSection.greenCoffee.bulkImport.noMillingOutputs') }}
        </p>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end py-4 px-6">
        <v-btn outlined color="primary" class="mr-3" min-width="120" :disabled="loading" @click="close">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn color="primary" class="white--text" min-width="120" :loading="loading" @click="save">
          {{ $t('memberDataSection.greenCoffee.bulkImport.submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { SERVER_ERROR_CODE_I18N } from "@/utils/greenCoffeeBulkImport";

const B = "memberDataSection.greenCoffee.bulkImport";

export default {
  name: "GreenCoffeeBulkImportEditDialog",
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    addedBy: { type: String, default: "" },
  },
  data() {
    return {
      form: {
        inputLotId: "",
        outturnNumber: "",
        growerCode: "",
        growerName: "",
        greenBeansTotal: "",
        coffeeType: "",
        qualityGradings: [],
      },
      // key → matches recordedJsonData / PUT payload field.
      headerFields: [
        { key: "inputLotId", labelKey: `${B}.editFieldId`, type: "text", unit: "" },
        {
          key: "outturnNumber",
          labelKey: "memberDataSection.greenCoffee.outturnNumber",
          type: "text",
          unit: "",
        },
        { key: "growerCode", labelKey: `${B}.growerCode`, type: "text", unit: "" },
        { key: "growerName", labelKey: `${B}.growerName`, type: "text", unit: "" },
        {
          key: "greenBeansTotal",
          labelKey: `${B}.inputWeightWithBags`,
          type: "number",
          unit: "Kg",
        },
        { key: "coffeeType", labelKey: `${B}.coffeeType`, type: "text", unit: "" },
      ],
      gradingFields: [
        { key: "cleanCoffee", labelKey: `${B}.millingCleanCoffee`, type: "text", unit: "" },
        { key: "bags", labelKey: `${B}.millingBags`, type: "number", unit: "" },
        { key: "pockets", labelKey: `${B}.millingPockets`, type: "number", unit: "kg" },
        { key: "weight", labelKey: `${B}.millingWeight`, type: "number", unit: "kg" },
        { key: "bulkOutturn", labelKey: `${B}.millingBulkOutturn`, type: "text", unit: "" },
        { key: "ratio", labelKey: `${B}.millingRatio`, type: "number", unit: "Kg" },
      ],
    };
  },
  watch: {
    record: {
      immediate: true,
      handler(rec) {
        this.syncForm(rec);
      },
    },
    value(open) {
      if (open) this.syncForm(this.record);
    },
  },
  methods: {
    syncForm(rec) {
      const data = (rec && rec.recordedJsonData) || {};
      this.form = {
        inputLotId: this.str(data.inputLotId),
        outturnNumber: this.str(data.outturnNumber),
        growerCode: this.str(data.growerCode),
        growerName: this.str(data.growerName),
        greenBeansTotal: this.str(data.greenBeansTotal),
        coffeeType: this.str(data.coffeeType),
        qualityGradings: (data.qualityGradings || []).map((lot) => ({
          cleanCoffee: this.str(lot.cleanCoffee),
          bags: this.str(lot.bags),
          pockets: this.str(lot.pockets),
          weight: this.str(lot.weight),
          bulkOutturn: this.str(lot.bulkOutturn),
          ratio: this.str(lot.ratio),
        })),
      };
    },
    str(v) {
      return v != null ? String(v) : "";
    },
    hasFieldError(field) {
      const md = (this.record && this.record.errorMetadata) || {};
      return Boolean(md[field] && md[field].length);
    },
    fieldMessages(field) {
      const md = (this.record && this.record.errorMetadata) || {};
      const codes = md[field] || [];
      return codes
        .map((code) => {
          const key = SERVER_ERROR_CODE_I18N[code];
          return key ? this.$t(`${B}.${key}`) : code;
        })
        .filter(Boolean);
    },
    close() {
      if (this.loading) return;
      this.$emit("input", false);
    },
    save() {
      const payload = {
        inputLotId: this.text(this.form.inputLotId),
        outturnNumber: this.text(this.form.outturnNumber),
        growerCode: this.text(this.form.growerCode),
        growerName: this.text(this.form.growerName),
        coffeeType: this.text(this.form.coffeeType),
        greenBeansTotal: this.num(this.form.greenBeansTotal),
        qualityGradings: this.form.qualityGradings.map((lot) => ({
          cleanCoffee: this.text(lot.cleanCoffee),
          bags: this.num(lot.bags),
          pockets: this.num(lot.pockets),
          weight: this.num(lot.weight),
          bulkOutturn: this.text(lot.bulkOutturn),
          ratio: this.num(lot.ratio),
        })),
      };
      this.$emit("save", { recordId: this.record.id, payload });
    },
    text(v) {
      const s = v == null ? "" : String(v).trim();
      return s === "" ? null : s;
    },
    num(v) {
      if (v === "" || v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.bulk-edit-body {
  max-height: 70vh;
}

.bulk-edit-section-title {
  color: var(--primary-color, #944a00);
  font-size: 18px;
}
</style>
